import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Tag } from 'antd';
import {
  FileTextOutlined, OpenAIOutlined, PlusOutlined, UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { FlexRightSpaced } from '../../components';
import OrderTypeContentHeaderComponent from './OrderTypeContentHeaderComponent';
import {
  ordersResponsePropTypes, OrderContext, OrderType, sortingPropTypes,
} from '../../types';
import ImportOrderModal from './Components/ImportOrderModal';
import AIImportOrderModal from './Components/AIImportOrderModal';
import Co2EmissionReportModal from './Components/Co2EmissionReportModal';
import { getSelectedCompany, isFeatureEnabled } from '../../store/selectors';
import { FeatureToggle } from '../../store/actions';

const OrdersHeaderComponent = ({
  size,
  orderContext,
  orders,
  sorting,
  interfaceLoading,
  refreshIntervalSeconds,
  activeType,
  filters,
  orderType,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAIModalVisible, setIsAIModalVisible] = useState(false);
  const [isCo2EmissionReportModalVisible, setIsCo2EmissionReportModalVisible] = useState(false);
  const contextCompanyId = useSelector(getSelectedCompany);
  const isAIImportEnabled = useSelector(isFeatureEnabled(FeatureToggle.MO_AI_IMPORT));
  const isCo2EmissionReportEnabled = useSelector(isFeatureEnabled(FeatureToggle.MO_CO2_EMISSION_REPORT));
  const shouldShowCo2EmissionReportButton = orderContext === OrderContext.OUT
    && orderType === OrderType.TOUR
    && isCo2EmissionReportEnabled;

  const openOrderCreationForm = () => {
    history.push('/orders/new');
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showAIModal = () => {
    setIsAIModalVisible(true);
  };

  const showCo2EmissionReportModal = () => {
    setIsCo2EmissionReportModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAICancel = () => {
    setIsAIModalVisible(false);
  };

  const handleCo2EmissionReportCancel = () => {
    setIsCo2EmissionReportModalVisible(false);
  };

  return (
    <>
      <FlexRightSpaced>
        <Button
          type="primary"
          size={size}
          icon={<PlusOutlined />}
          onClick={openOrderCreationForm}
        >
          {t('ORDER.CREATE_MONITORING')}
        </Button>
        <Button
          type="primary"
          size={size}
          icon={<UploadOutlined />}
          onClick={showModal}
        >
          {t('ORDER.IMPORT_FILE')}
        </Button>
        {isAIImportEnabled && (
          <Button
            type="primary"
            size={size}
            icon={<OpenAIOutlined />}
            onClick={showAIModal}
          >
            {t('ORDER.IMPORT_WITH_AI')}
            <Tag className="beta-tag">{t('ORDER.IMPORT_ORDER.BETA')}</Tag>
          </Button>
        )}
        {shouldShowCo2EmissionReportButton && (
          <Button
            type="primary"
            size={size}
            icon={<FileTextOutlined />}
            onClick={showCo2EmissionReportModal}
          >
            {t('CO2_EMISSION_REPORT.BUTTON')}
          </Button>
        )}
        <OrderTypeContentHeaderComponent
          orderContext={orderContext}
          orders={orders}
          sorting={sorting}
          interfaceLoading={interfaceLoading}
          refreshIntervalSeconds={refreshIntervalSeconds}
          activeType={activeType}
          filters={filters}
          orderType={orderType}
        />
      </FlexRightSpaced>
      <ImportOrderModal
        visible={isModalVisible}
        onCancel={handleCancel}
        onSuccess={onSuccess}
        contextCompanyId={contextCompanyId}
      />
      <AIImportOrderModal
        visible={isAIModalVisible}
        onCancel={handleAICancel}
        onSuccess={onSuccess}
        contextCompanyId={contextCompanyId}
      />
      <Co2EmissionReportModal
        visible={isCo2EmissionReportModalVisible}
        onCancel={handleCo2EmissionReportCancel}
        contextCompanyId={contextCompanyId}
      />
    </>
  );
};

OrdersHeaderComponent.propTypes = {
  size: PropTypes.string,
  orderContext: PropTypes.oneOf(Object.values(OrderContext)).isRequired,
  orderType: PropTypes.oneOf(Object.values(OrderType)),
  orders: ordersResponsePropTypes.isRequired,
  sorting: sortingPropTypes.isRequired,
  interfaceLoading: PropTypes.bool.isRequired,
  refreshIntervalSeconds: PropTypes.number,
  activeType: PropTypes.string.isRequired,
  filters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.any)),
  onSuccess: PropTypes.func.isRequired, // Add propType for onSuccess
};

OrdersHeaderComponent.defaultProps = {
  size: 'small',
  orderType: OrderType.TIME,
  refreshIntervalSeconds: 0,
  filters: {},
};

export default OrdersHeaderComponent;
